import { useState, useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { Layout, Dropdown, Avatar, Button, App, Space, Tag, Typography } from 'antd'
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  AppstoreOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import type { RoleModel } from '@ay-shared-core/types/admin'
import SideMenu from '@/components/menu/SideMenu'
import PageBreadcrumb from '@/components/breadcrumb/PageBreadcrumb'
import { getProfile, logout } from '@/api/admin'
import { clearTokens, getUserInfo, setUserInfo, clearUserInfo } from '@/utils/token'

const { Header, Sider, Content } = Layout
const { Text } = Typography

const MainLayout = () => {
  const { message } = App.useApp()
  const [collapsed, setCollapsed] = useState(false)
  const [userNickName, setUserNickName] = useState(() => getUserInfo()?.nick_name || '管理员')
  const [userRoles, setUserRoles] = useState<RoleModel[]>(() => getUserInfo()?.roles ?? [])
  const [profileVersion, setProfileVersion] = useState(0)
  const navigate = useNavigate()

  // 获取用户信息（每次挂载都从服务器获取，确保角色等数据最新）
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const resp = await getProfile()
        if (resp.code === 0 && resp.data) {
          setUserInfo(resp.data)
          setUserNickName(resp.data.nick_name || '')
          setUserRoles(resp.data.roles ?? [])
          setProfileVersion((v) => v + 1)
        }
      } catch (e) {
        console.error('获取用户信息失败:', e)
      }
    }
    loadProfile()

    // 监听 storage 事件，Profile 页面更新后刷新右上角昵称
    const handleStorage = () => {
      const info = getUserInfo()
      if (info) {
        setUserNickName(info.nick_name || '')
        setUserRoles(info.roles ?? [])
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  const handleLogout = async () => {
    const resp = await logout()
    clearTokens()
    clearUserInfo()
    message.success(resp.code === 0 ? resp.msg : '退出登录成功')
    navigate('/login')
  }

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <SettingOutlined />,
      label: '个人信息',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ]

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        theme="light"
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={220}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: '0 16px',
            gap: 12,
          }}
        >
          <AppstoreOutlined style={{ fontSize: 24, color: '#1677ff' }} />
          {!collapsed && (
            <span style={{ fontSize: 18, fontWeight: 600, color: 'rgba(0, 0, 0, 0.88)' }}>
              天昌物流管理系统
            </span>
          )}
        </div>
        <SideMenu key={profileVersion} />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 220, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            padding: '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: 16,
            }}
          />
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
            <Button type="text" style={{ height: 40, padding: '8px 12px' }}>
              <Space align="center" size={8}>
                <Avatar icon={<UserOutlined />} size="small" />
                <Text>{userNickName}</Text>
                {userRoles.map((role) => (
                  <Tag key={role.id} color="blue" style={{ margin: 0 }}>
                    {role.name}
                  </Tag>
                ))}
              </Space>
            </Button>
          </Dropdown>
        </Header>
        <Content
          style={{
            margin: 24,
            padding: 0,
            minHeight: 280,
          }}
        >
          <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
            <PageBreadcrumb />
            <Outlet />
          </Space>
        </Content>
      </Layout>
    </Layout>
  )
}

export default MainLayout
